import {AccountMarginSummaries} from "../../../models/AccountPerformanceSummary";
import {OptionSummary} from "../models/OptionSummary";
import {IsoDate} from "app/pseudo-types/DateTypes";
import {Pair, Predicate} from "../../../../types/GenericTypes";
import {
    HistoricalExerciseTrades,
    HistoricalOptionTrade,
    HistoricalOptionTrades, HistoricalTradeData
} from "../../../models/HistoricalTradeData";
import {closingSummary, openingSummary} from "./SummaryCalculatorUtil";
import {sum} from "../../../../js/util/UtilFunctions";

export class OptionSummaryCalculator {
    public static calculate(options: HistoricalOptionTrades,
                            cash: HistoricalExerciseTrades,
                            range: Pair<IsoDate>,
                            marginHistory: AccountMarginSummaries): OptionSummary {

        const startDate = range[0];
        const endDate = range[1];

        const endSummary = closingSummary(endDate, marginHistory);

        const asAtValue = options.filter(OptionSummaryCalculator.isAsAtOption(startDate))
                                 .map((o) => o.value)
                                 .reduce(sum, 0);

        const openTradesValue = options.filter(OptionSummaryCalculator.isCurrentlyOpenOption(endDate))
                                       .map((o) => o.value)
                                       .reduce(sum, 0);

        const closedTrades = options.filter(OptionSummaryCalculator.isClosedPosition(startDate, endDate));
        const cashSettled = cash.filter(OptionSummaryCalculator.isClosedPosition(startDate, endDate));

        const closedTradesValue = closedTrades.map((o) => o.value)
                                              .reduce(sum, 0);

        const openValue = (endSummary.premiumMargin * -1) + openTradesValue;
        const closeValue = closedTradesValue - openTradesValue;

        const cashSettlement = cashSettled.map((c) => c.value).reduce(sum, 0);
        const fees = [...closedTrades, ...cashSettled].map((o) => o.totalFees).reduce(sum, 0);
        const brokerage = [...closedTrades, ...cashSettled].map((o) => o.totalBrokerage).reduce(sum, 0);

        const totalIncomes = openTradesValue + asAtValue + closeValue + cashSettlement;
        const totalCosts = brokerage + fees;
        const pnl = totalIncomes - totalCosts;

        return {
            asAtValue,
            openValue,
            closeValue,
            brokerage,
            fees,
            cashSettlement,
            pnl
        };
    }

    private static isAsAtOption(asAt: IsoDate): Predicate<HistoricalOptionTrade> {
        return (opt) => (opt.expiry > asAt) && (opt.tradeDate < asAt);
    }

    private static isCurrentlyOpenOption(asAt: IsoDate): Predicate<HistoricalOptionTrade> {
        return (opt) => (opt.expiry > asAt) && (opt.tradeDate < asAt);
    }

    private static isClosedPosition(startDate: IsoDate, endDate: IsoDate): Predicate<HistoricalTradeData> {
        return (opt) => (opt.tradeDate >= startDate) && (opt.tradeDate <= endDate);
    }
}
